import React, { useEffect, useState } from "react";
import { getAiSettings, setAiSettings, testAiSettings } from "../lib/api";
import { Button } from "./ui/button";
import { Cpu, Check, Zap } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_LABEL = { openai: "OpenAI", anthropic: "Anthropic Claude", gemini: "Google Gemini" };

export const AiEngineCard = () => {
  const [models, setModels] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");

  useEffect(() => {
    getAiSettings().then((d) => {
      setModels(d.models || []);
      const cur = { provider: d.provider, model: d.model };
      setCurrent(cur); setSelected(cur);
    }).catch(() => {});
  }, []);

  const isSel = (m) => selected && m.provider === selected.provider && m.model === selected.model;
  const isCur = (m) => current && m.provider === current.provider && m.model === current.model;
  const dirty = selected && current && (selected.provider !== current.provider || selected.model !== current.model);

  const save = async () => {
    setBusy(true);
    try {
      const d = await setAiSettings(selected);
      setCurrent({ provider: d.provider, model: d.model });
      toast.success("Motore AI aggiornato");
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setReply("");
    try {
      const d = await testAiSettings(selected);
      setReply(d.reply);
      toast.success("Il modello risponde");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test fallito"); }
    finally { setBusy(false); }
  };

  const groups = ["openai", "anthropic", "gemini"];

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="ai-engine-card">
      <div className="mb-1 flex items-center gap-2">
        <Cpu className="h-5 w-5 text-primary" strokeWidth={1.75} />
        <h3 className="font-heading text-lg font-semibold">Motore AI</h3>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Scegli quale modello usare per score, mini-audit, Scopri AI e messaggi. Tutti funzionano con la Universal Key già configurata.
      </p>

      <div className="space-y-4">
        {groups.map((g) => (
          <div key={g}>
            <div className="label-caps mb-2 text-muted-foreground">{PROVIDER_LABEL[g]}</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {models.filter((m) => m.provider === g).map((m) => (
                <button key={m.model} type="button" onClick={() => setSelected({ provider: m.provider, model: m.model })}
                  data-testid={`ai-model-${m.model}`}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors ${
                    isSel(m) ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}>
                  <div>
                    <div className="text-sm font-medium">{m.label}</div>
                    <div className="text-xs text-muted-foreground">{m.note}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isCur(m) && <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[0.6rem] uppercase tracking-wide text-primary" data-testid="ai-model-active">attivo</span>}
                    {isSel(m) && <Check className="h-4 w-4 text-primary" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy || !dirty} data-testid="ai-save-btn">
          <Check className="mr-2 h-4 w-4" /> Usa questo modello
        </Button>
        <Button variant="outline" onClick={test} disabled={busy || !selected} data-testid="ai-test-btn">
          <Zap className="mr-2 h-4 w-4" /> {busy ? "..." : "Testa risposta"}
        </Button>
      </div>
      {reply && (
        <div className="mt-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm italic" data-testid="ai-test-reply">“{reply}”</div>
      )}
    </div>
  );
};
